import { Component, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { NotifierService } from 'angular-notifier';
import { ReplaySubject, Subject, firstValueFrom, take, takeUntil } from 'rxjs';
import { BankAccountDto, SendAccountReceiverDto } from 'src/app/domain/models/dto/bank-account.dto';
import { BankDashDto, BankDto } from 'src/app/domain/models/dto/bank.dto';
import { CorporateAccountDto } from 'src/app/domain/models/dto/corporate-account.dto';
import { AuthenticationService } from 'src/app/shared/services';
import { BankAccountService } from 'src/app/shared/services/bank-account.service';
import { CorporateAccountService } from 'src/app/shared/services/corporate-account.service';
import { OrdersService } from 'src/app/shared/services/orders.service';
 
@Component({
  selector: 'app-bank-account',
  templateUrl: './bank-account.component.html',
  styleUrls: ['./bank-account.component.scss'],
})
export class BankAccountComponent implements OnInit {
  private readonly notifier: NotifierService;
  accountForm: FormGroup;
  tenant: string = '';
  banks: BankDashDto[] = [];
  userAccounts: BankAccountDto[];
  companyId: number;
  loading: boolean;
  public bankFilterCtrl = new FormControl('');
  public filteredBanks: ReplaySubject<BankDashDto[]> = new ReplaySubject<
    BankDashDto[]
  >(1);

  @ViewChild('singleSelect', { static: true }) singleSelect: MatSelect;

  protected _onDestroy = new Subject<void>();

  constructor(
    fb: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private corporateAccountService: CorporateAccountService,
    private bankAccountService: BankAccountService,
    private authService: AuthenticationService,
    notifierService: NotifierService
  ) {
    this.notifier = notifierService;
    this.companyId = +this.authService.getCompanyId();
    this.accountForm = fb.group({
      id: [null],
      name: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      number: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      agency: ['', [Validators.required, Validators.pattern('^[0-9]+$')]],
    });
  }

  get accountFormControl() {
    return this.accountForm.controls;
  }

  get account(): any {
    const account = this.accountForm.getRawValue();
    return account;
  }

  ngOnInit() {
    firstValueFrom(this.bankAccountService.getNewBanks()).then((value) => {
      this.banks = value.data;

      this.filteredBanks.next(this.banks);
    });

    this.route.paramMap.subscribe((params) => {
      const result = params.get('tenant');

      if (result) this.tenant = params.get('tenant');
    });

    this.bankFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterBanks();
      });

    this.bankAccountService.getAll().subscribe((getResult) => {
      this.userAccounts = getResult.data;
      if (getResult.data[0]) {
        getResult.data[0]?.id ? this.accountForm.controls['id'].setValue(
          getResult.data[0]?.id
        ) : null;
        this.accountForm.controls['name'].setValue(
          getResult.data[0]?.name
        );
        this.accountForm.controls['agency'].setValue(
          getResult.data[0]?.agency
        );
        this.accountForm.controls['number'].setValue(
          getResult.data[0]?.number
        );
      }
    });
  }

  changeBank(event: any) {
    this.accountForm.controls['name'].setValue(event.value);
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  protected setInitialValue() {
    this.filteredBanks
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.singleSelect.compareWith = (a: BankDashDto, b: BankDashDto) =>
          a && b && a.id === b.id;
      });
  }

  protected filterBanks() {
    if (!this.banks) {
      return;
    }
    let search = this.bankFilterCtrl.value;

    if (!search) {
      this.filteredBanks.next(this.banks);
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filteredBanks.next(
      this.banks.filter((bank) => {
        const searchLower = search.toLowerCase();
        return (
          bank.name.toLowerCase().indexOf(searchLower) > -1 ||
          bank.code.indexOf(search) > -1
        );
      })
    );
  }

  goBack() {
    this.router.navigate([`${this.tenant}/onboardings/responsible`]);
  }

  goNext() {
    this.router.navigate([`${this.tenant}/onboardings/responsible-documents`]);
  }
  
  sendAccountInbound() {
    const account = new BankAccountDto();
    const bank = this.banks.find((item: any) => item.name === this.account.name);
  
    account.id = this.account.id ? this.account.id : null;
    account.name = this.account.name;
    account.code = bank?.code;
    account.agency = this.account.agency;
    account.number = this.account.number;
  
    this.loading = true;
  
      this.corporateAccountService.sendAccountReceiver(account, this.companyId).subscribe({
        next: (res) => {
          this.loading = false;
          this.goNext();
        },
        error: (err) => {
          this.loading = false;
        },
      });
    
  }
}
